'use client'

import { PropsWithChildren, useEffect, useRef, useState } from 'react'
import { usePathname, useRouter } from 'next/navigation'

import { OnboardingLoader } from '@common/apps'

import { InitializationContext } from '../contexts/InitializationContext'
import { useAppInitialization } from '../hooks/useAppInitialization'
import { useAuthUser } from '../hooks/useAuthUser'
import { useFirstTimeUserPortalRedirect } from '../hooks/useFirstTimeUserPortalRedirect'
import { isAcceptInvitationRoute } from '../utils/isAcceptInvitationRoute'

export function InitializationProvider({ children }: PropsWithChildren) {
  const pathname = usePathname()
  const router = useRouter()
  const { user: auth0User } = useAuthUser()
  const [isEmailVerified, setIsEmailVerified] = useState(auth0User?.email_verified === true)
  const hasRefreshedRouterRef = useRef(false)
  const { isReady, isInitializing, hasInitialized, initializationError, isOnboarding } =
    useAppInitialization(isEmailVerified)

  useFirstTimeUserPortalRedirect({
    isEmailVerified,
    isInitializing,
    isOnboarding,
  })

  useEffect(() => {
    if (!auth0User?.email_verified) return

    setIsEmailVerified(true)

    if (hasRefreshedRouterRef.current) return
    hasRefreshedRouterRef.current = true
    router.refresh()
  }, [auth0User?.email_verified, router])

  if (isOnboarding && !isAcceptInvitationRoute(pathname)) return <OnboardingLoader />

  return (
    <InitializationContext
      value={{
        isReady,
        isInitializing,
        hasInitialized,
        initializationError,
        isEmailVerified,
        setIsEmailVerified,
      }}
    >
      {children}
    </InitializationContext>
  )
}
